import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { trpc } from "../../../../api/trpc/trpc"
import { getCurrency } from "../../../../i18n/config"
import { LegendItem } from "../../../../components/chart/ChartLegend"
import { EnergyUnit } from "@nano-portal/shared"

const prepareChartData = (data: {
  pricesInTime?: Map<number, number>
  productionsInTime?: Map<number, number>
  installedPower: number
}) => {
  if (!data.pricesInTime || !data.productionsInTime) {
    return []
  }

  const result: { marketPrice: number; production: number; month: number }[] = []
  const { pricesInTime, productionsInTime, installedPower } = data
  for (const [time, averagePrice] of pricesInTime) {
    const monthlyProduction = productionsInTime.get(time) || 0

    result.push({
      marketPrice: averagePrice,
      production: monthlyProduction * installedPower,
      month: time,
    })
  }

  return result
}

export const useDailyProductionController = () => {
  const [filter, setFilter] = useState({ spotPrices: true, production: true })
  const [installedPower, setInstalledPower] = useState<number | undefined>(1)
  const { t } = useTranslation(["microsite"])

  const pricesQuery = trpc.useQuery(["web.monthlyPricesInTime"])
  const productionsQuery = trpc.useQuery(["web.monthlyProductions"])

  const legendItems: LegendItem[] = useMemo(
    () => [
      {
        color: "primary",
        label: t("microsite:market_price_with_unit", {
          currency: getCurrency().symbol,
          unit: EnergyUnit.kWh,
        }),
        hidden: !filter.spotPrices,
        onClick: () => setFilter((prev) => ({ ...prev, spotPrices: !prev.spotPrices })),
      },
      {
        color: "textsAlt",
        label: t("microsite:daily_price_household_production", {
          unit: EnergyUnit.kWh,
        }),
        hidden: !filter.production,
        onClick: () => setFilter((prev) => ({ ...prev, production: !prev.production })),
      },
    ],
    [t, filter]
  )

  return {
    isError: productionsQuery.isError || pricesQuery.isError,
    isLoading: productionsQuery.isLoading || pricesQuery.isLoading,
    chartData: prepareChartData({
      pricesInTime: pricesQuery.data,
      productionsInTime: productionsQuery.data?.productionsInTime,
      installedPower: installedPower ?? 0,
    }),
    total: (productionsQuery.data?.totalProductionValue ?? 0) * (installedPower ?? 0),
    installedPower,
    setInstalledPower,
    filter,
    legendItems,
  }
}
